<!-- src/admin/pages/ContactMessagesPage.vue -->
<template>
  <div class="messages-container">
    <header class="messages-header">
      <h1>Contact Us Submissions</h1>
    </header>

    <div v-if="messages.length === 0" class="no-messages">
      No contact submissions found.
    </div>

    <div v-else>
      <!-- Column Selector Dropdown -->
      <div class="column-selector">
        <button class="columns-button" @click="showColumnMenu = !showColumnMenu">
          Columns ▾
        </button>
        <div v-if="showColumnMenu" class="column-menu">
          <label v-for="(label, key) in columnLabels" :key="key">
            <input type="checkbox" v-model="columns[key]" />
            {{ label }}
          </label>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrap">
        <table class="messages-table">
          <thead>
            <tr>
              <th v-if="columns.name">Name</th>
              <th v-if="columns.email">Email</th>
              <th v-if="columns.phone">Phone</th>
              <th v-if="columns.subject">Subject</th>
              <th v-if="columns.sentAt">Date Sent</th>
              <th v-if="columns.subscribed">Subscribed</th>
              <th v-if="columns.message">Message</th>
              <th v-if="columns.status">Status</th>
              <th v-if="columns.actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="msg in messages" :key="msg.id">
              <!-- Normal row: show only the columns flagged as visible -->
              <tr>
                <td v-if="columns.name">{{ msg.name }}</td>
                <td v-if="columns.email">{{ msg.email }}</td>
                <td v-if="columns.phone">{{ msg.phone }}</td>
                <td v-if="columns.subject">{{ msg.subject }}</td>
                <td v-if="columns.sentAt">{{ formatDate(msg.sentAt) }}</td>
                <td v-if="columns.subscribed">
                  <span
                    class="sub-badge"
                    :class="{ yes: msg.subscribed, no: !msg.subscribed }"
                  >
                    {{ msg.subscribed ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td v-if="columns.message" class="message-text">{{ msg.message }}</td>
                <td v-if="columns.status">
                  <span
                    class="reply-status"
                    :class="{ replied: msg.replied, pending: !msg.replied }"
                  >
                    {{ msg.replied ? 'Replied' : 'Pending' }}
                  </span>
                </td>
                <td v-if="columns.actions">
                  <button
                    class="action-button reply"
                    :disabled="msg.replied || replyingTo === msg.id"
                    @click="startReply(msg)"
                  >
                    Reply
                  </button>
                </td>
              </tr>

              <!-- Inline reply form -->
              <tr v-if="replyingTo === msg.id && !msg.replied">
                <td :colspan="visibleCount" class="reply-form-row">
                  <div class="reply-form">
                    <textarea
                      v-model="replyText"
                      placeholder="Type your reply here..."
                      rows="4"
                    ></textarea>
                    <div class="reply-form-buttons">
                      <button class="action-button send" @click="sendReply(msg)">
                        Send
                      </button>
                      <button class="action-button cancel" @click="cancelReply()">
                        Cancel
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { notifyWarning, notifySuccess, notifyError } from '@/services/notify'
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc
} from 'firebase/firestore'
import { db } from '@/firebase'

// ---------------------------------------------------------------------
// 1) Firestore: load all contact messages in real time
// ---------------------------------------------------------------------
const messages = ref([])
let unsubscribe = null

onMounted(() => {
  const msgsRef = collection(db, 'contactMessages')
  const msgsQuery = query(msgsRef, orderBy('sentAt', 'desc'))

  unsubscribe = onSnapshot(
    msgsQuery,
    (snapshot) => {
      messages.value = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
    },
    (error) => {
      console.error('Error reading contactMessages:', error)
    }
  )
})

onUnmounted(() => {
  if (unsubscribe) unsubscribe()
})

// ---------------------------------------------------------------------
// 2) Column Visibility State & Dropdown
// ---------------------------------------------------------------------
const showColumnMenu = ref(false)

// Define which columns can be toggled, and their display labels:
const columnLabels = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  subject: 'Subject',
  sentAt: 'Date Sent',
  subscribed: 'Subscribed',
  message: 'Message',
  status: 'Status',
  actions: 'Actions'
}

// Reactive object to track visibility:
const columns = reactive({
  name: true,
  email: true,
  phone: true,
  subject: true,
  sentAt: true,
  subscribed: true,
  message: true,
  status: true,
  actions: true
})

// Compute count of visible columns to set `colspan` on reply‐form row:
const visibleCount = computed(() => {
  return Object.values(columns).filter((v) => v).length
})

// ---------------------------------------------------------------------
// 3) Reactive state for the inline “Reply” form
// ---------------------------------------------------------------------
const replyingTo = ref(null) // holds the msg.id being replied to
const replyText = ref('')    // the admin’s reply content

function startReply(msg) {
  replyingTo.value = msg.id
  replyText.value = ''
}

function cancelReply() {
  replyingTo.value = null
  replyText.value = ''
}

// ---------------------------------------------------------------------
// 4) Send the reply via AWS Lambda AND update Firestore
// ---------------------------------------------------------------------
const SEND_EMAIL_URL =
  'https://qmqivl7jmyshlmtxjgqae5li540ejbeo.lambda-url.us-east-2.on.aws/'

async function sendReply(msg) {
  if (!replyText.value.trim()) {
    notifyWarning('Please type your reply before sending.', 'Reply is empty')
    return
  }

  // 4a) Build Lambda payload
  const payload = {
    recipient_email: msg.email,
    subject: `Re: ${msg.subject}`,
    message: `
      <p>Hi ${msg.name},</p>
      <p>${sanitize(replyText.value)}</p>
      <hr>
      <p><em>Original message sent on ${formatDate(msg.sentAt)}:</em></p>
      <blockquote>${sanitize(msg.message)}</blockquote>
    `.trim()
  }

  try {
    // 4b) POST to Lambda to send email
    const response = await fetch(SEND_EMAIL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        `Failed to send email: ${errorData.message || response.statusText}`
      )
    }

    // 4c) Mark this message as replied in Firestore
    const msgDocRef = doc(db, 'contactMessages', msg.id)
    await updateDoc(msgDocRef, {
      replied: true,
      replyText: replyText.value.trim(),
      repliedAt: new Date()
    })

    notifySuccess('Your reply was sent and the message is marked as replied.', 'Reply sent')
  } catch (err) {
    console.error('Error sending reply email:', err)
    notifyError(`The reply could not be sent: ${err.message}`, 'Email failed')
  } finally {
    cancelReply()
  }
}

// ---------------------------------------------------------------------
// 5) Helper: Format Firestore Timestamp to “MMM D, YYYY hh:mm”
// ---------------------------------------------------------------------
function formatDate(ts) {
  if (!ts) return '—'
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
  return (
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) +
    ' ' +
    d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    })
  )
}

// ---------------------------------------------------------------------
// 6) Helper: Sanitize user-generated content to prevent HTML injection
// ---------------------------------------------------------------------
function sanitize(input) {
  const div = document.createElement('div')
  div.textContent = input
  return div.innerHTML
}
</script>

<style scoped>
/* -------------------------------------------------------------------
   Container & Header
   ------------------------------------------------------------------- */
.messages-container {
  background-color: #f0f4f8;
  padding: 24px;
  min-height: 100%;
}

.messages-header h1 {
  font-size: 24px;
  margin-bottom: 16px;
  color: #1f2937;
}

.no-messages {
  text-align: center;
  color: #555;
  margin-top: 2rem;
}

/* -------------------------------------------------------------------
   Column Selector Styles
   ------------------------------------------------------------------- */
.column-selector {
  position: relative;
  margin-bottom: 12px;
}
.columns-button {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}
.columns-button:hover {
  background-color: #f3f4f6;
}
.column-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 10;
}
.column-menu label {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 14px;
  color: #374151;
}
.column-menu input {
  margin-right: 6px;
}

/* -------------------------------------------------------------------
   Table & Rows
   ------------------------------------------------------------------- */
.table-wrap {
  overflow-x: auto;
}

.messages-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #ffffff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.messages-table thead {
  background-color: #e5e7eb;
}

.messages-table th,
.messages-table td {
  padding: 12px 16px;
  text-align: left;
  color: #374151;
  vertical-align: top;
}

.messages-table th {
  font-size: 12px;
  text-transform: uppercase;
  color: #6b7280;
}

.messages-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

/* -------------------------------------------------------------------
   Message Text Wrapping
   ------------------------------------------------------------------- */
.message-text {
  max-width: 300px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* -------------------------------------------------------------------
   Subscribed & Status Badges
   ------------------------------------------------------------------- */
.sub-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.sub-badge.yes {
  background-color: #d1fae5;
  color: #065f46;
}

.sub-badge.no {
  background-color: #fef3c7;
  color: #92400e;
}

.reply-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.reply-status.pending {
  background-color: #fef3c7;
  color: #92400e;
}

.reply-status.replied {
  background-color: #d1fae5;
  color: #065f46;
}

/* -------------------------------------------------------------------
   Action Buttons (Reply / Send / Cancel)
   ------------------------------------------------------------------- */
.action-button {
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* Reply button (disabled when already replied or form open) */
.action-button.reply {
  background-color: #3b82f6;
  color: white;
}
.action-button.reply[disabled] {
  background-color: #93c5fd;
  cursor: not-allowed;
}
.action-button.reply:hover:not([disabled]) {
  background-color: #2563eb;
}

/* Inline Reply Form Styles */
.reply-form-row {
  background-color: #f9fafb;
}
.reply-form {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.reply-form textarea {
  width: 100%;
  resize: vertical;
  padding: 8px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  min-height: 80px;
}
.reply-form-buttons {
  display: flex;
  gap: 8px;
}

.action-button.send {
  background-color: #10b981;
  color: white;
}
.action-button.send:hover {
  background-color: #059669;
}

.action-button.cancel {
  background-color: #ef4444;
  color: white;
}
.action-button.cancel:hover {
  background-color: #dc2626;
}

/* -------------------------------------------------------------------
   Responsive Adjustments
   ------------------------------------------------------------------- */
@media (max-width: 768px) {
  .messages-table th,
  .messages-table td {
    padding: 8px 12px;
  }
  .message-text {
    max-width: 150px;
  }
  .reply-form textarea {
    min-height: 60px;
  }
}
</style>
